import { useState } from "react";
import { AlertCircle, Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import {
  CANCELLATION_DETAILS_MAX_LENGTH,
  CANCELLATION_MODE_COPY,
  CANCELLATION_REASONS,
  type CancellationMode,
  type CancellationReason,
} from "./cancellation";

const NOTICE_CLASSNAMES: Record<CancellationMode, string> = {
  direct: "border-destructive/30 bg-destructive/5 text-red-800 dark:text-red-200",
  ticket: "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200",
};

interface CancellationFormProps {
  mode: CancellationMode;
  isPending: boolean;
  errorMessage: string | null;
  onSubmit: (request: CancellationReason) => void;
  onKeep: () => void;
}

export const CancellationForm = ({
  mode,
  isPending,
  errorMessage,
  onSubmit,
  onKeep,
}: CancellationFormProps) => {
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const copy = CANCELLATION_MODE_COPY[mode];
  const hasReason = reason !== "";

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!hasReason || isPending) return;
    onSubmit({ reason, details });
  };

  return (
    <form onSubmit={handleSubmit} className="flex max-w-[620px] flex-col gap-5 p-7">
      <h2 className="text-[19px] font-bold leading-tight">Cancelar este pedido</h2>

      <div className="flex flex-col gap-2">
        <Label htmlFor="cancellation-reason" className="font-semibold">
          Motivo do cancelamento
        </Label>
        <Select value={reason} onValueChange={setReason} disabled={isPending}>
          <SelectTrigger id="cancellation-reason" aria-required="true" className="h-12 text-[14.5px]">
            <SelectValue placeholder="Selecione um motivo" />
          </SelectTrigger>
          <SelectContent>
            {CANCELLATION_REASONS.map((option) => (
              <SelectItem key={option} value={option}>
                {option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-col gap-2">
        <Label htmlFor="cancellation-details" className="font-semibold">
          Observação <span className="font-normal text-muted-foreground">(opcional)</span>
        </Label>
        <Textarea
          id="cancellation-details"
          value={details}
          onChange={(e) => setDetails(e.target.value.slice(0, CANCELLATION_DETAILS_MAX_LENGTH))}
          maxLength={CANCELLATION_DETAILS_MAX_LENGTH}
          aria-describedby="cancellation-details-counter"
          className="min-h-[88px] resize-y text-[14.5px]"
          disabled={isPending}
        />
        <span id="cancellation-details-counter" className="self-end text-xs text-muted-foreground/70">
          {CANCELLATION_DETAILS_MAX_LENGTH - details.length} caracteres restantes
        </span>
      </div>

      <p className={cn("rounded-lg border px-4 py-3.5 text-[13.5px] leading-relaxed", NOTICE_CLASSNAMES[mode])}>
        {copy.notice}
      </p>

      {errorMessage && (
        <Alert variant="destructive" role="alert">
          <AlertCircle className="h-4 w-4" aria-hidden="true" />
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <Button type="submit" variant="destructive" disabled={!hasReason || isPending}>
          {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
          {isPending ? copy.pendingLabel : copy.submitLabel}
        </Button>
        <Button
          type="button"
          variant="ghost"
          onClick={onKeep}
          disabled={isPending}
          className="text-muted-foreground hover:bg-transparent hover:text-foreground"
        >
          Voltar
        </Button>
      </div>
    </form>
  );
};
